import React, { useEffect, useState, useContext } from "react";
import { useLocation, useNavigate, useParams, useOutletContext } from 'react-router-dom';
import { Card } from "../../common_components/Layout";
import { TitleAndText } from "../components/TitleAndText";
import { FormField, NumberField, IntegerField, DropdownQuestion, DropdownOption, NewDropdownOption, NewDropdownQuestion, NewIntegerField } from "../../common_components/Forms";
import CreationFooter from "../components/CreationFooter";
import { useAlert } from 'react-alert'
import CenteredLoadingIcon from "../../common_components/CenteredLoadingIcon";
import MissionAttendeesForm from "../components/MissionAttendeesForm";
import { ResearcherContext } from "../../../../ResearcherContext";
import CreateMissionHeaderAndTitleRow from "../components/CreateMissionHeaderAndTitleRow";
import { updatePrice, PriceDisplay } from "../components/PriceDisplay";
import InfoIcon from "../../../../../../../assets/images/info_icon_3.png"


// Api Calls
import MissionsAPI from "../../../api/api_calls/MissionAPI";


function SessionDetails(props) {

    const navigate = useNavigate();
    const alert = useAlert();
    const { missionId } = useParams()
    const [submitInProcess, setSubmitInProcess] = useState(false);
    const [loadingMission, setLoadingMission] = useState(true);

    const [missionType, loadingMissionType, nextPage, backPage] = [props.missionType, props.loadingMissionType, props.nextPage, props.backPage]
    const { researcher, setResearcher, refreshResearcher } = useContext(ResearcherContext);

    const [sessionLength, setSessionLength] = useState(-1);
    const [numberRespondents, setNumberRespondents] = useState(-1);
    const [videoRequired, setVideoRequired] = useState(false);
    const [attendees, setAttendees] = useState([]);
    const [timezone, setTimezone] = useState("");
    const [deviceRequirement, setDeviceRequirement] = useState(0);
    const [requireShareScreen, setRequireShareScreen] = useState(false);
    const [requireWalletUsage, setRequireWalletUsage] = useState(false);

    const sessionLengthTooltip = "The length of each feedback session."
    const numberRespondentsTooltip = "The total number of sessions you'd like to have"
    const colleagesTooltip = "Colleagues that will also receive calendar invites for sessions. They will not automatically be invited to this platform. Type in their emails separated by commas."
    const deviceRequirementTooltip = "Ensure the user will use a mobile or desktop device for the session. Slightly increases price."

    const requireShareScreenTooltip = "Ask the user to share their screen during the session."
    const requireWalletUsageTooltip = "Ask the user to use their wallet (e.g. connect to a website/dapp) during the session. Increases price."
    const requireVideoTooltip = "Ask the user to turn on video during the session."

    const mayProgress = sessionLength > 0 && numberRespondents > 0 && attendees.length > 0;

    const deviceRequirementLabels = {
        "any": "Any device",
        "desktop": "Require Desktop",
        "mobile": "Require Mobile",
    }

    const [price, setPrice] = useState("-");
    const [totalPrice, setTotalPrice] = useState("-");

    function updatePriceWrapper() {
        updatePrice(setPrice, setTotalPrice, missionId, navigate, alert);
    }


    // -------------------------------------
    // ------ Event Handlers ---------------
    // -------------------------------------

    function onClickBack() {
        navigate(backPage, { state: { missionId: missionId } })
    }

    // Create the mission and move to the next page.
    async function createSessionDetails({ sessionLengthData = sessionLength, numberRespondentsData = numberRespondents, videoRequiredData = videoRequired, attendeesData = attendees, deviceRequirementData = deviceRequirement, requireShareScreenData = requireShareScreen, requireWalletUsageData = requireWalletUsage }) {
        setSubmitInProcess(true);
        const result = await MissionsAPI.updateSessionDetails(missionId, sessionLengthData, numberRespondentsData, videoRequiredData, attendeesData, timezone, deviceRequirementData, requireShareScreenData, requireWalletUsageData, navigate);

        if (!result.success) {
            alert.show(result.error, { type: 'error' })
        }
        setSubmitInProcess(false);
    }

    function onClickSubmit(event = null) {
        if (event != null) { event.preventDefault() };

        // Vet the answers all make sense.
        if (sessionLength <= 0) {
            alert.show("Please select a session length", { type: 'error' })
            return false;
        }
        if (numberRespondents <= 0) {
            alert.show("Please select a number of respondents", { type: 'error' })
            return false;
        }
        if (attendees.length == 0) {
            alert.show("Please add at least one colleague", { type: 'error' })
            return false;
        }
        navigate(nextPage, { state: { missionId: missionId } });
    }


    // -------------------------------------
    // ------ Use Effects ---------------
    // -------------------------------------


    useEffect(() => {
        // Enable tooltips
        setTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone)

        async function getSessionDetails() {
            const result = await MissionsAPI.show(missionId, navigate);

            if (result.success) {
                if (result.mission_event) {
                    setSessionLength(result.mission_event.appointment_length_in_milliseconds * 1 / 1000 / 60);
                    setNumberRespondents(result.mission.num_total_spots * 1);
                    setVideoRequired(result.mission_event.video_required);
                    setAttendees(result.mission_attendees);
                    setDeviceRequirement(result.mission.device_requirement);
                    setRequireShareScreen(result.mission.require_share_screen);
                    setRequireWalletUsage(result.mission.require_wallet_usage);
                }
                else {
                    setAttendees(result.mission_attendees ? result.mission_attendees : []);
                }
                setLoadingMission(false);
            } else {
                alert.show(result.error, { type: 'error' })
            }
            const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]')
            const tooltipList = [...tooltipTriggerList].map(tooltipTriggerEl => new bootstrap.Tooltip(tooltipTriggerEl))
        }

        getSessionDetails();
        props.setAllowedToProgress(mayProgress)
    }, []);

    useEffect(() => {
        props.setAllowedToProgress(mayProgress)
    }, [mayProgress]);


    // -------------------------------------
    // ------ Helper Functions ---------------
    // -------------------------------------

    function onSessionLengthChange(event) {
        console.log("setting new length: ", event.target.value)
        setSessionLength(event.target.value);
        createSessionDetails({ sessionLengthData: event.target.value });
    }
    function onNumberRespondentsChange(event) {
        setNumberRespondents(event.target.value);
        createSessionDetails({ numberRespondentsData: event.target.value });
    }
    function onDeviceRequirementChange(event) {
        setDeviceRequirement(event.target.value);
        createSessionDetails({ deviceRequirementData: event.target.value });
    }
    function onRequireVideoChange(event) {
        const newValue = event.target.checked;
        setVideoRequired(newValue);
        createSessionDetails({ videoRequiredData: newValue });
    }
    function onRequireShareScreenChange(event) {
        const newValue = event.target.checked;
        setRequireShareScreen(newValue);
        createSessionDetails({ requireShareScreenData: newValue });
    }
    function onRequireWalletUsageChange(event) {
        const newValue = event.target.checked;
        setRequireWalletUsage(newValue);
        createSessionDetails({ requireWalletUsageData: newValue });
    }

    // Attendees functions.
    function addAttendee({ first_name, last_name, email }) {
        const attendee = { id: `temp_${email}`, first_name: first_name, last_name: last_name, email: email, removable: true }
        // Check if the email is already in the list
        if (attendees.find(att => att.email == email)) {
            alert.show("This email is already added", { type: 'error' })
            return;
        }
        setAttendees([...attendees, attendee]);
        createSessionDetails({ attendeesData: [...attendees, attendee] });
    }
    function deleteAttendee(id) {
        setAttendees(attendees.filter(attendee => attendee.id != id));
        createSessionDetails({ attendeesData: attendees.filter(attendee => attendee.id != id) });
    }



    return (

        <div className="create-mission-primary-content-container">
            <CreateMissionHeaderAndTitleRow title={"Session Details"} subtitle="Set up your live feedback sessions" price={price} totalPrice={totalPrice} hidePrice={true} />
            <SessionDetailsForm>
                <div className="session-details-container">
                    {loadingMission ? <CenteredLoadingIcon /> :
                        (<>
                            <div className="session-details-form-container">
                                <NewDropdownQuestion questionText="Session Length" label="session_length" name="session_length" id="sessionLength" onChange={onSessionLengthChange} placeholder="Select length" defaultValue={sessionLength} tooltip={sessionLengthTooltip} >
                                    <NewDropdownOption value="15">15 minutes</NewDropdownOption>
                                    <NewDropdownOption value="30">30 minutes</NewDropdownOption>
                                    <NewDropdownOption value="45">45 minutes</NewDropdownOption>
                                    <NewDropdownOption value="60">60 minutes</NewDropdownOption>
                                    <NewDropdownOption value="90">90 minutes</NewDropdownOption>
                                </NewDropdownQuestion>
                                <NewIntegerField label="Number of Respondents" name="number_of_respondents" id="numrespondents"
                                    placeholder="Enter number" onChange={onNumberRespondentsChange}
                                    tooltip={numberRespondentsTooltip} min={1} step={1}
                                    value={numberRespondents >= 0 ? numberRespondents : ""} />
                                <NewDropdownQuestion questionText="Require Mobile or Desktop?" label="device_requirement" name="device_requirement" id="deviceRequirement" onChange={onDeviceRequirementChange} defaultValue={deviceRequirement} tooltip={deviceRequirementTooltip} >
                                    <NewDropdownOption value="any">{deviceRequirementLabels["any"]}</NewDropdownOption>
                                    <NewDropdownOption value="desktop">{deviceRequirementLabels["desktop"]}</NewDropdownOption>
                                    <NewDropdownOption value="mobile">{deviceRequirementLabels["mobile"]}</NewDropdownOption>
                                </NewDropdownQuestion>
                                <div className="researcher-label-and-question-div">
                                    <div className="researcher-label-and-tooltip-div">
                                        <div className="researcher-label">Other Session Requirements</div>
                                    </div>
                                    <div className="researcher-horizontal-separate-questions">
                                        <CheckboxAndLabel label="Require Video" value={videoRequired} toggleCheckBox={onRequireVideoChange} tooltip={requireVideoTooltip} />
                                        <CheckboxAndLabel label="Require Share Screen" value={requireShareScreen} toggleCheckBox={onRequireShareScreenChange} tooltip={requireShareScreenTooltip} />
                                        <CheckboxAndLabel label="Require Wallet Usage" value={requireWalletUsage} toggleCheckBox={onRequireWalletUsageChange} tooltip={requireWalletUsageTooltip} />
                                    </div>
                                </div>
                                <MissionAttendeesForm attendees={attendees} addAttendee={addAttendee} deleteAttendee={deleteAttendee} />
                            </div>
                            <CreationFooter buttonText="Next" onClickBack={onClickBack} onClickSubmit={onClickSubmit} submitLoading={submitInProcess} />
                        </>
                        )}
                </div>
            </SessionDetailsForm>
        </div>

    )
}

function SessionDetailsForm(props) {

    return (
        <div className="session-details-form" >
            {props.children}
        </div>
    )
}

function CheckboxAndLabel(props) {
    return (
        <div className="user-type-checkbox-container">
            <input className="user-type-checkbox-color"
                id={props.label}
                type='checkbox'
                name={props.label}
                onChange={props.toggleCheckBox}
                checked={props.value}
            />
            <label className="extra-padding-medium gray-text" htmlFor={props.label}>
                {props.label}
            </label>
            {props.tooltip && <img className="" src={InfoIcon} width={16} height={16} data-bs-toggle="tooltip" data-bs-placement="top" data-bs-title={props.tooltip} />}
        </div>
    )
}

export default SessionDetails;